import { useMutation } from '@tanstack/react-query';
import { useApi } from 'contexts/ApiContext';
import type Long from 'long';

import { getEventTypeForAction } from '@polyai/stream/commands/mapper/DONOTEDIT';
import { Command, CommandBatch } from '@polyai/stream/protobuf/commands';

import type { CommandBatchResponse } from 'api/resources/commandBatch/types';
import type { ActionWithCommandId } from '../types';

interface SendCommandsParams {
  accountId: string;
  projectId: string;
  branchId: string;
}

export const useCommandSender = ({
  accountId,
  projectId,
  branchId,
}: SendCommandsParams) => {
  const api = useApi();

  return useMutation<
    CommandBatchResponse,
    Error,
    {
      lastKnownSequence: Long;
      actions: ActionWithCommandId[];
    }
  >({
    mutationFn: ({ lastKnownSequence, actions }) => {
      const commands = actions.map(({ commandId, action }) => {
        const eventType = getEventTypeForAction(action);

        if (!eventType) {
          throw new Error(
            `Could not find command mapping for event ${action.type}`,
          );
        }

        return Command.create({
          commandId,
          metadata: {
            createdAt: action.meta.createdAt
              ? new Date(action.meta.createdAt)
              : undefined,
            createdBy: action.meta.createdBy,
          },
          payload: {
            $case: eventType as any,
            value: action.payload,
          },
        });
      });

      const batch = CommandBatch.create({
        lastKnownSequence,
        commands,
      });

      if (process.env.NEXT_PUBLIC_POLYAI_ENVIRONMENT === 'development') {
        const bin = CommandBatch.encode(batch).finish();
        const base64 = btoa(String.fromCharCode(...bin));

        console.log('Sending command batch :', base64);
      }

      return api.methods.sendCommandBatch({
        accountId,
        projectId,
        branchId,
        batch,
      });
    },
    meta: {
      isDraftChange: true,
    },
  });
};
